import { Plugin, ButtonView, ContextualBalloon, clickOutsideHandler } from 'ckeditor5';
import { v4 as uuid } from 'uuid';
import { ConditionFormView } from './ConditionFormView';
import { InsertConditionCommand } from './insertCommand';
import { ConditionsSchema } from './schema';
export class ConditionsUI extends Plugin {
    static get pluginName() {
        return 'ConditionsUI';
    }
    init() {
        const { editor } = this;
        this.insertCommand = editor.commands.get(InsertConditionCommand.pluginName);
        this.balloon = editor.plugins.get(ContextualBalloon);
        this.formView = this.createFormView();
        editor.ui.componentFactory.add('insertCondition', () => {
            const button = new ButtonView();
            button.bind('isEnabled').to(this.insertCommand);
            button.set({
                withText: true,
                label: 'Create Condition',
                class: 'pleditor-toolbar-fa-icon-button',
                tooltip: 'Add conditional marker to selected text',
            });
            button.on('execute', () => {
                this.insertCommand.addCondition({
                    id: uuid(),
                    type: 'assume',
                });
            });
            return button;
        });
        editor.ui.componentFactory.add('editCondition', () => {
            const button = new ButtonView();
            button.set({
                withText: true,
                label: 'Edit Condition',
                class: 'pleditor-toolbar-fa-icon-button',
                tooltip: 'Edit the condition at cursor position',
                isEnabled: false,
            });
            this.listenTo(editor.model.document.selection, 'change:range', () => {
                const markers = this.getMarkersAtSelection();
                button.isEnabled = markers.length > 0;
            });
            button.on('execute', () => {
                this.showEditForm();
            });
            return button;
        });
    }
    createFormView() {
        const { editor } = this;
        const formView = new ConditionFormView(editor.locale);
        this.listenTo(formView, 'submit', () => {
            const { conditionId, conditionType } = formView;
            this.updateCondition(conditionId, conditionType);
            this.hideEditForm();
        });
        this.listenTo(formView, 'cancel', () => {
            this.hideEditForm();
        });
        this.listenTo(formView, 'delete', () => {
            const markers = this.getMarkersAtSelection();
            if (markers.length === 0)
                return;
            editor.model.change((writer) => {
                markers.forEach((marker) => {
                    writer.removeMarker(marker.name);
                });
            });
            this.hideEditForm();
        });
        clickOutsideHandler({
            emitter: formView,
            activator: () => this.balloon.visibleView === formView,
            contextElements: [this.balloon.view.element],
            callback: () => this.hideEditForm(),
        });
        return formView;
    }
    showEditForm() {
        const markers = this.getMarkersAtSelection();
        if (markers.length === 0)
            return;
        const marker = markers[0];
        const parts = marker.name.split(':');
        const [, type, id] = parts;
        this.formView.conditionId = id;
        this.formView.conditionType = type;
        this.balloon.add({
            view: this.formView,
            position: this.getBalloonPositionData(),
        });
        this.formView.focus();
    }
    hideEditForm() {
        if (this.balloon.hasView(this.formView)) {
            this.balloon.remove(this.formView);
        }
        this.editor.editing.view.focus();
    }
    updateCondition(newId, newType) {
        const { editor } = this;
        const markers = this.getMarkersAtSelection();
        if (markers.length === 0)
            return;
        const marker = markers[0];
        const parts = marker.name.split(':');
        const [markerType, , , instanceId] = parts;
        editor.model.change((writer) => {
            const range = marker.getRange();
            const newMarkerName = `${markerType}:${newType}:${newId}:${instanceId || uuid()}`;
            writer.removeMarker(marker.name);
            writer.addMarker(newMarkerName, { range, usingOperation: true, affectsData: true });
        });
        const editing = editor.plugins.get(ConditionsSchema);
        editing.state.addCondition(`${newType}:${newId}`);
    }
    getBalloonPositionData() {
        const { editor } = this;
        const { view } = editor.editing;
        const viewDocument = view.document;
        const target = view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange());
        return { target };
    }
    getMarkersAtSelection() {
        const { editor } = this;
        const { selection } = editor.model.document;
        const position = selection.getFirstPosition();
        if (!position)
            return [];
        const markers = Array.from(editor.model.markers.getMarkersAtPosition(position));
        const conditionMarkers = markers.filter((marker) => marker.name.startsWith('condition-block:') || marker.name.startsWith('condition-inline:'));
        // If there are nested conditions, return only the innermost one
        if (conditionMarkers.length > 1) {
            // Find the marker with the smallest range (innermost)
            return [
                conditionMarkers.reduce((innermost, current) => {
                    const innermostSize = innermost.getRange().end.offset - innermost.getRange().start.offset;
                    const currentSize = current.getRange().end.offset - current.getRange().start.offset;
                    return currentSize < innermostSize ? current : innermost;
                }),
            ];
        }
        return conditionMarkers;
    }
}
